/* ==========================================================================
   Virsaay — The House of Virsaay Controller
   Handles masked editorial text reveals, sequential clip-path image reveals,
   and subtle internal dual-plane image parallax with GSAP + ScrollTrigger.
   ========================================================================== */

(function () {
  'use strict';

  function waitForMotion(callback) {
    if (window.VirsaayMotion && window.VirsaayMotion.ready) {
      window.VirsaayMotion.ready(callback);
      return;
    }
    var attempts = 0;
    var timer = setInterval(function () {
      attempts++;
      if (window.VirsaayMotion && window.VirsaayMotion.ready) {
        clearInterval(timer);
        window.VirsaayMotion.ready(callback);
      } else if (attempts > 60) {
        clearInterval(timer);
        if (window.gsap && window.ScrollTrigger) {
          callback(window.gsap, window.ScrollTrigger);
        }
      }
    }, 50);
  }

  function initVirsaayHouse(section) {
    if (!section || section.dataset.tvhInitialized === 'true') return;
    section.dataset.tvhInitialized = 'true';

    var eyebrow = section.querySelector('[data-tvh-eyebrow]');
    var headlineLines = section.querySelectorAll('.tvh-line');
    var bodyWrap = section.querySelector('[data-tvh-body]');
    var linkWrap = section.querySelector('[data-tvh-link]');
    var linkRule = section.querySelector('.tvh-link-rule');
    var primaryFrame = section.querySelector('[data-tvh-primary]');
    var secondaryFrame = section.querySelector('[data-tvh-secondary]');
    var primaryImg = section.querySelector('.tvh-img--primary');
    var secondaryImg = section.querySelector('.tvh-img--secondary');
    var metaMarker = section.querySelector('.tvh-meta-marker');
    var captionStrip = section.querySelector('.tvh-caption-strip');

    if (!headlineLines.length && !primaryFrame) return;

    waitForMotion(function (gsap, ScrollTrigger) {
      if (!gsap || !ScrollTrigger) return;

      // Accessibility: Respect reduced motion immediately
      if (window.VirsaayMotion && window.VirsaayMotion.reduced) {
        gsap.set([eyebrow, headlineLines, bodyWrap, linkWrap, metaMarker, captionStrip], {
          opacity: 1,
          y: 0
        });
        if (linkRule) gsap.set(linkRule, { scaleX: 1, width: '36px', opacity: 0.45 });
        if (primaryFrame) gsap.set(primaryFrame, { clipPath: 'inset(0% 0% 0% 0%)', opacity: 1 });
        if (secondaryFrame) gsap.set(secondaryFrame, { clipPath: 'inset(0% 0% 0% 0%)', opacity: 1 });
        return;
      }

      gsap.registerPlugin(ScrollTrigger);

      var ctx = gsap.context(function () {
        var isMobile = window.innerWidth < 990;

        // ------------------------------------------------------------------
        // Initial States
        // ------------------------------------------------------------------
        if (eyebrow) gsap.set(eyebrow, { opacity: 0, y: 10 });
        if (headlineLines.length) gsap.set(headlineLines, { opacity: 0, y: 80 });
        if (bodyWrap) gsap.set(bodyWrap, { opacity: 0, y: 20 });
        if (linkWrap) gsap.set(linkWrap, { opacity: 0, y: 15 });
        if (linkRule) gsap.set(linkRule, { scaleX: 0, transformOrigin: 'left center' });
        if (metaMarker) gsap.set(metaMarker, { opacity: 0, y: 8 });
        if (captionStrip) gsap.set(captionStrip, { opacity: 0 });

        // Initial image reveal state: vertical clip-path inset(12% 0% 0% 0%)
        if (primaryFrame) {
          gsap.set(primaryFrame, {
            clipPath: 'inset(12% 0% 0% 0%)',
            opacity: 0.85
          });
        }
        if (secondaryFrame) {
          gsap.set(secondaryFrame, {
            clipPath: 'inset(12% 0% 0% 0%)',
            opacity: 0.85
          });
        }

        // ------------------------------------------------------------------
        // Sequential Editorial Reveal Timeline
        // ------------------------------------------------------------------
        var revealTl = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: 'top 75%',
            toggleActions: 'play none none none'
          }
        });

        // 1. Section label: fade in (0.7s)
        if (eyebrow) {
          revealTl.to(eyebrow, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: 'power2.out'
          }, 0);
        }

        if (metaMarker) {
          revealTl.to(metaMarker, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: 'power2.out'
          }, 0.05);
        }

        // 2. Headline: Reveal line-by-line via overflow mask (duration 1.0s, stagger 0.1s, power3.out)
        if (headlineLines.length) {
          revealTl.to(headlineLines, {
            opacity: 1,
            y: 0,
            duration: 1.0,
            ease: 'power3.out',
            stagger: 0.1
          }, 0.1);
        }

        // 3. Body Copy: Reveal after headline (duration 0.7s, y 20 -> 0)
        if (bodyWrap) {
          revealTl.to(bodyWrap, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: 'power2.out'
          }, 0.45);
        }

        // 4. Primary Image: Vertical clip-path reveal (duration 1.3s, power3.out)
        if (primaryFrame) {
          revealTl.to(primaryFrame, {
            clipPath: 'inset(0% 0% 0% 0%)',
            opacity: 1,
            duration: 1.3,
            ease: 'power3.out'
          }, 0.2);
        }

        // 5. Secondary Image: Delayed vertical clip-path reveal (duration 1.0s, delay 0.2s)
        if (secondaryFrame) {
          revealTl.to(secondaryFrame, {
            clipPath: 'inset(0% 0% 0% 0%)',
            opacity: 1,
            duration: 1.0,
            ease: 'power3.out'
          }, 0.4);
        }

        if (captionStrip) {
          revealTl.to(captionStrip, {
            opacity: 1,
            duration: 0.8,
            ease: 'power2.out'
          }, 0.6);
        }

        // 6. Our Story link: reveals last; horizontal line draws from 0 to full width
        if (linkWrap) {
          revealTl.to(linkWrap, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: 'power2.out'
          }, 0.65);
        }

        if (linkRule) {
          revealTl.to(linkRule, {
            scaleX: 1,
            duration: 0.8,
            ease: 'power3.out'
          }, 0.65);
        }

        // ------------------------------------------------------------------
        // Image Parallax: Subtle Internal Independent Movement
        // Primary: yPercent -4 -> 4
        // Secondary: yPercent 4 -> -4
        // ------------------------------------------------------------------
        if (!isMobile) {
          if (primaryImg) {
            gsap.fromTo(primaryImg, {
              yPercent: -4
            }, {
              yPercent: 4,
              ease: 'none',
              scrollTrigger: {
                trigger: section,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.8
              }
            });
          }

          if (secondaryImg) {
            gsap.fromTo(secondaryImg, {
              yPercent: 4
            }, {
              yPercent: -4,
              ease: 'none',
              scrollTrigger: {
                trigger: section,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.8
              }
            });
          }
        }
      }, section);

      // Recompute triggers once loader finishes or page settles
      if (window.VirsaayMotion && window.VirsaayMotion.on) {
        window.VirsaayMotion.on('loader:done', function () {
          ScrollTrigger.refresh();
        });
      }

      window.addEventListener('load', function () {
        ScrollTrigger.refresh();
      }, { once: true });

      if (window.VirsaayMotion && window.VirsaayMotion.register) {
        var allTriggers = ScrollTrigger.getAll();
        if (allTriggers.length) {
          window.VirsaayMotion.register(section, allTriggers[allTriggers.length - 1]);
        }
      }

      section._tvhCtx = ctx;
    });
  }

  function initAll() {
    var sections = document.querySelectorAll('[data-virsaay-house]');
    for (var i = 0; i < sections.length; i++) {
      initVirsaayHouse(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify Section Lifecycle Events
  document.addEventListener('shopify:section:load', function (evt) {
    var el = evt.target.querySelector('[data-virsaay-house]') || evt.target;
    if (el && el.hasAttribute('data-virsaay-house')) {
      el.dataset.tvhInitialized = '';
      initVirsaayHouse(el);
    }
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var el = evt.target.querySelector('[data-virsaay-house]') || evt.target;
    if (el && el._tvhCtx) {
      el._tvhCtx.revert();
    }
  });
})();
